import { useState } from "react";
import {
  Box,
  Button,
  Dialog,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import ArrowLeftIcon from "@mui/icons-material/ArrowLeft";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import CreateNewFolderOutlinedIcon from "@mui/icons-material/CreateNewFolderOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import WorkspacesOutlinedIcon from "@mui/icons-material/WorkspacesOutlined";
import { motion, AnimatePresence } from "framer-motion";
import ProjectDetailsStep from "./ProjectDetailsStep";
import ProjectAssetsStep from "./ProjectAssetsStep";
import ProjectUsersStep from "./ProjectUsersStep";
import useCreateProjectModal from "./hooks/useCreateProjectModal";
import {
  getProjectId,
  isProjectArchived,
} from "../../utils/projectUtils";
import {
  DARK_BORDER,
  DIALOG_PAPER_SX,
  DIALOG_SX,
  FOOTER_BOTTOM_PADDING,
  stepContentCardSx,
  stepMotionVariants,
  THEME_ACCENT,
  WIZARD_STEPS,
} from "./shared";

const ARCHIVE_RED = "#F87171";
const ACTIVE_GREEN = "#4ADE80";

export default function CreateProjectModal({
  open,
  onClose,
  onSubmit,
  loading = false,
  clientId = null,
  mode = "create",
  editTarget = null,
  defaultStep = null,
  onArchiveToggle,
}) {
  const [archiveSaving, setArchiveSaving] = useState(false);
  const {
    activeStep,
    allUsers,
    canGoNext,
    canGoPrevious,
    canSubmit,
    clientProjects,
    clientProjectsLoading,
    contentRef,
    creatorUser,
    deletingKnowledgeFileNames,
    downloadingFileNames,
    error,
    existingKnowledgeFiles,
    existingKnowledgeLoading,
    form,
    handleDialogClose,
    handleDownloadExistingFile,
    handleNextStep,
    handlePreviousStep,
    handleRemoveExistingKnowledgeFile,
    handleRemoveUser,
    handleSelectedUsersChange,
    handleStepClick,
    handleSubmit,
    handleUpdateUserRoles,
    isAdmin,
    isEditMode,
    projectRoles,
    projectRolesLoading,
    projectTypes,
    projectTypesLoading,
    setForm,
    userAssignments,
    usersLoading,
  } = useCreateProjectModal({
    open,
    onClose,
    onSubmit,
    clientId,
    mode,
    editTarget,
    defaultStep,
  });

  const editProjectId = isEditMode ? getProjectId(editTarget) : null;
  const archivedProject = isEditMode && isProjectArchived(editTarget);
  const currentStepMeta = WIZARD_STEPS[activeStep];

  const handleArchiveToggle = async () => {
    if (!isEditMode || editProjectId == null || !onArchiveToggle || archiveSaving) {
      return;
    }

    setArchiveSaving(true);
    try {
      await onArchiveToggle(editTarget, !archivedProject);
      onClose?.();
    } finally {
      setArchiveSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleDialogClose}
      maxWidth={false}
      sx={DIALOG_SX}
      PaperProps={{ sx: DIALOG_PAPER_SX }}
    >
      <Box
        sx={{
          px: 3,
          pt: 2.5,
          pb: 2.25,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          height: "100%",
          boxSizing: "border-box",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            mb: 2.5,
            flexShrink: 0,
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                width: 42,
                height: 42,
                borderRadius: "12px",
                display: "grid",
                placeItems: "center",
                background: `${THEME_ACCENT}22`,
                border: `1px solid ${THEME_ACCENT}44`,
              }}
            >
              <WorkspacesOutlinedIcon sx={{ color: THEME_ACCENT, fontSize: 22 }} />
            </Box>
            <Box>
              <Typography sx={{ fontSize: 18, fontWeight: 700, letterSpacing: "-0.01em" }}>
                {isEditMode ? "Edit Project" : "Create New Project"}
              </Typography>
              <Typography sx={{ fontSize: 12, color: "rgba(255,255,255,0.45)", mt: 0.2 }}>
                {`Step ${activeStep + 1} of ${WIZARD_STEPS.length} · ${currentStepMeta.label}`}
              </Typography>
            </Box>
          </Stack>
          <IconButton
            onClick={onClose}
            aria-label="Close"
            sx={{
              width: 34,
              height: 34,
              borderRadius: "8px",
              border: `1px solid ${DARK_BORDER}`,
              color: "rgba(255,255,255,0.5)",
              flexShrink: 0,
              "&:hover": { color: "#fff", background: "rgba(255,255,255,0.06)" },
            }}
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>

        <Box
          sx={{
            mb: 2.5,
            flexShrink: 0,
            px: { xs: 0, sm: 0.5 },
          }}
          role="navigation"
          aria-label="Create project wizard steps"
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              width: "100%",
            }}
          >
            {WIZARD_STEPS.map((step, index) => {
              const isActive = index === activeStep;
              const isCompleted = index < activeStep;
              const isLast = index === WIZARD_STEPS.length - 1;
              const Icon = step.icon;
              const canNavigate = typeof handleStepClick === "function";

              return (
                <Box
                  key={step.id}
                  sx={{
                    flex: isLast ? "0 0 auto" : 1,
                    display: "flex",
                    alignItems: "flex-start",
                    minWidth: 0,
                  }}
                >
                  <Box
                    component="button"
                    type="button"
                    onClick={() => canNavigate && handleStepClick(index)}
                    disabled={!canNavigate}
                    aria-current={isActive ? "step" : undefined}
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 0.75,
                      border: "none",
                      background: "none",
                      cursor: canNavigate ? "pointer" : "default",
                      p: 0,
                      flexShrink: 0,
                      minWidth: { xs: 52, sm: 72 },
                      "&:disabled": { cursor: "default" },
                      "&:focus-visible": {
                        outline: `2px solid ${THEME_ACCENT}`,
                        outlineOffset: 4,
                        borderRadius: "8px",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: 38,
                        height: 38,
                        borderRadius: "50%",
                        display: "grid",
                        placeItems: "center",
                        transition: "all 250ms ease",
                        background: isActive
                          ? `${THEME_ACCENT}22`
                          : isCompleted
                            ? `${THEME_ACCENT}14`
                            : "rgba(255,255,255,0.05)",
                        border: isActive
                          ? `2px solid ${THEME_ACCENT}`
                          : isCompleted
                            ? `1.5px solid ${THEME_ACCENT}88`
                            : "1.5px solid rgba(255,255,255,0.12)",
                        boxShadow: isActive ? `0 0 18px ${THEME_ACCENT}33` : "none",
                      }}
                    >
                      {isCompleted ? (
                        <CheckIcon sx={{ fontSize: 18, color: THEME_ACCENT }} />
                      ) : (
                        <Icon
                          sx={{
                            fontSize: 17,
                            color: isActive ? THEME_ACCENT : "rgba(255,255,255,0.35)",
                          }}
                        />
                      )}
                    </Box>
                    <Typography
                      sx={{
                        fontSize: { xs: 10, sm: 11.5 },
                        fontWeight: isActive ? 700 : 500,
                        color: isActive
                          ? "#fff"
                          : isCompleted
                            ? THEME_ACCENT
                            : "rgba(255,255,255,0.4)",
                        textAlign: "center",
                        lineHeight: 1.25,
                        letterSpacing: "0.01em",
                      }}
                    >
                      <Box component="span" sx={{ display: { xs: "none", md: "inline" } }}>
                        {step.label}
                      </Box>
                      <Box component="span" sx={{ display: { xs: "inline", md: "none" } }}>
                        {step.shortLabel}
                      </Box>
                    </Typography>
                  </Box>

                  {!isLast ? (
                    <Box
                      sx={{
                        flex: 1,
                        height: 2,
                        mx: { xs: 0.25, sm: 0.75 },
                        mt: "18px",
                        borderRadius: 1,
                        minWidth: 8,
                        background: isCompleted
                          ? `linear-gradient(90deg, ${THEME_ACCENT}, ${THEME_ACCENT}66)`
                          : "rgba(255,255,255,0.08)",
                        transition: "background 300ms ease",
                      }}
                    />
                  ) : null}
                </Box>
              );
            })}
          </Box>
        </Box>

        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <AnimatePresence mode="wait" initial={false}>
            <Box
              key={activeStep}
              component={motion.div}
              variants={stepMotionVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.22, ease: "easeOut" }}
              sx={{
                flex: 1,
                minHeight: 0,
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Box sx={stepContentCardSx}>
                <Box
                  ref={contentRef}
                  sx={{
                    flex: 1,
                    minHeight: 0,
                    overflowY: "auto",
                    px: { xs: 2, sm: 2.75 },
                    py: { xs: 2, sm: 2.5 },
                    "&::-webkit-scrollbar": { width: 5 },
                    "&::-webkit-scrollbar-thumb": {
                      bgcolor: "rgba(255,255,255,0.12)",
                      borderRadius: 999,
                    },
                  }}
                >
                  {activeStep === 0 ? (
                    <ProjectDetailsStep
                      clientId={clientId}
                      clientProjects={clientProjects}
                      clientProjectsLoading={clientProjectsLoading}
                      form={form}
                      open={open}
                      activeStep={activeStep}
                      projectTypes={projectTypes}
                      projectTypesLoading={projectTypesLoading}
                      setForm={setForm}
                    />
                  ) : null}

                  {activeStep === 1 ? (
                    <ProjectAssetsStep
                      form={form}
                      setForm={setForm}
                      existingKnowledgeFiles={isEditMode ? existingKnowledgeFiles : []}
                      existingKnowledgeLoading={isEditMode ? existingKnowledgeLoading : false}
                      deletingKnowledgeFileNames={isEditMode ? deletingKnowledgeFileNames : []}
                      downloadingKnowledgeFileNames={isEditMode ? downloadingFileNames : []}
                      onDownloadExistingFile={
                        isEditMode ? handleDownloadExistingFile : undefined
                      }
                      onRemoveExistingKnowledgeFile={
                        isEditMode ? handleRemoveExistingKnowledgeFile : undefined
                      }
                    />
                  ) : null}

                  {activeStep === 2 ? (
                    <ProjectUsersStep
                      allUsers={allUsers}
                      creatorUser={creatorUser}
                      isAdmin={isAdmin}
                      onRemoveUser={handleRemoveUser}
                      onSelectedUsersChange={handleSelectedUsersChange}
                      onUpdateRoles={handleUpdateUserRoles}
                      projectRoles={projectRoles}
                      projectRolesLoading={projectRolesLoading}
                      userAssignments={userAssignments}
                      usersLoading={usersLoading}
                    />
                  ) : null}
                </Box>
              </Box>
            </Box>
          </AnimatePresence>
        </Box>

        <Box
          sx={{
            flexShrink: 0,
            pt: 2,
            mt: 2,
            borderTop: `1px solid ${DARK_BORDER}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            flexWrap: "wrap",
            pb: FOOTER_BOTTOM_PADDING,
          }}
        >
          {isEditMode && onArchiveToggle ? (
            <Box
              component="button"
              type="button"
              onClick={handleArchiveToggle}
              disabled={editProjectId == null || archiveSaving}
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.75,
                border: "none",
                background: "none",
                cursor:
                  editProjectId == null || archiveSaving
                    ? "not-allowed"
                    : "pointer",
                p: 0,
                font: "inherit",
                fontSize: 13,
                fontWeight: 500,
                color: archivedProject ? ACTIVE_GREEN : ARCHIVE_RED,
                opacity: archiveSaving ? 0.65 : 1,
              }}
            >
              <DeleteOutlineIcon
                sx={{
                  fontSize: 20,
                  color: archivedProject ? ACTIVE_GREEN : ARCHIVE_RED,
                }}
              />
              {archiveSaving
                ? "Saving..."
                : archivedProject
                  ? "Restore project"
                  : "Archive project"}
            </Box>
          ) : (
            <Typography
              sx={{
                fontSize: 12,
                color: "rgba(255,255,255,0.4)",
                fontWeight: 500,
              }}
            >
              Step {activeStep + 1} of {WIZARD_STEPS.length}
            </Typography>
          )}

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              gap: 1.25,
              flexWrap: "wrap",
              ml: "auto",
            }}
          >
            {error ? (
              <Typography sx={{ fontSize: 12.5, color: "#ff8b8b", mr: "auto" }}>
                {error}
              </Typography>
            ) : null}

            {canGoPrevious ? (
              <Button
                onClick={handlePreviousStep}
                startIcon={<ArrowLeftIcon sx={{ fontSize: 18 }} />}
                sx={{
                  minWidth: 96,
                  py: 0.9,
                  textTransform: "none",
                  fontWeight: 600,
                  borderRadius: "10px",
                  color: "#cbd5e1",
                  border: "1px solid rgba(255,255,255,0.28)",
                  "&:hover": { background: "rgba(255,255,255,0.08)" },
                }}
              >
                Back
              </Button>
            ) : null}

            {canGoNext ? (
              <Button
                onClick={handleNextStep}
                endIcon={<ArrowRightIcon sx={{ fontSize: 18 }} />}
                sx={{
                  minWidth: 120,
                  py: 0.9,
                  textTransform: "none",
                  fontWeight: 600,
                  borderRadius: "10px",
                  color: "#cbd5e1",
                  border: "1px solid rgba(255,255,255,0.28)",
                  "&:hover": { background: "rgba(255,255,255,0.08)" },
                }}
              >
                Continue
              </Button>
            ) : null}

            <Button
              onClick={handleSubmit}
              disabled={loading || !canSubmit}
              startIcon={<CreateNewFolderOutlinedIcon />}
              sx={{
                minWidth: 140,
                py: 0.9,
                textTransform: "none",
                fontWeight: 600,
                borderRadius: "10px",
                background: canSubmit && !loading ? THEME_ACCENT : "rgba(255,255,255,0.08)",
                color: canSubmit && !loading ? "#111" : "rgba(255,255,255,0.35)",
                boxShadow: canSubmit && !loading ? `0 4px 20px ${THEME_ACCENT}44` : "none",
                "&:hover": canSubmit && !loading
                  ? {
                      background: THEME_ACCENT,
                      filter: "brightness(1.08)",
                      boxShadow: `0 6px 22px ${THEME_ACCENT}55`,
                    }
                  : {},
                "&.Mui-disabled": {
                  background: "rgba(255,255,255,0.08)",
                  color: "rgba(255,255,255,0.3)",
                  boxShadow: "none",
                },
              }}
            >
              {loading
                ? isEditMode
                  ? "Saving..."
                  : "Creating..."
                : isEditMode
                  ? "Save Changes"
                  : "Create Project"}
            </Button>
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
}
